import React, { useEffect, useState } from "react";
import { List, Space, Typography } from "antd";
import { BellOutlined, ShoppingOutlined } from "@ant-design/icons";
import { formatInZone } from "../../../../utils/time";
import SectionCard from "../../../../components/shared/SectionCard";
import InlineLoading from "../../../../components/shared/InlineLoading";
import EmptyState from "../../../../components/shared/EmptyState";
import {
	fetchSentNotifications,
} from "../../../../api/communications/sentNotificationsApi";
import { SentNotification } from "../../../../types/communications/NotificationTypes";
import { fetchSalesOrders } from "../../../../api/inventory/salesOrderApi";
import { SalesOrder } from "../../../../types/inventory/OrderTypes";
import { Customer } from "../../../../types/inventory/CustomerTypes";
import { customerMoney } from "../../../../types/inventory/customerRecord";
import CustomerLevelSection from "./CustomerLevelSection";

const { Text } = Typography;

/**
 * CU-86d44j2ac — customer-safe Overview: recent notifications and orders only, nothing clinical
 * whatever the row's current account type.
 */
const RECENT_LIMIT = 5;

interface CustomerOverviewTabProps {
	customer: Customer;
	/** CU-86d45b54n — adopts the server summary returned by the Customer Level control. */
	onCustomerChange: (customer: Customer) => void;
	/** CU-14ygtkj7wuy — the drawer's unsaved-profile-edit state, forwarded to the conversion control. */
	hasUnsavedChanges?: boolean;
}

const CustomerOverviewTab: React.FC<CustomerOverviewTabProps> = ({
	customer,
	onCustomerChange,
	hasUnsavedChanges = false,
}) => {
	const [notifications, setNotifications] = useState<SentNotification[]>([]);
	const [orders, setOrders] = useState<SalesOrder[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setLoading(true);

			const [sent, sales] = await Promise.all([
				fetchSentNotifications({ recipient_user_id: customer.id }).catch(() => []),
				fetchSalesOrders({
					assignee_type: "user",
					assignee_id: customer.id,
					page: 1,
					per_page: RECENT_LIMIT,
					sort_field: "sales_order_id",
					sort_direction: "desc",
				}).catch(() => ({ data: [], total: 0 })),
			]);

			if (cancelled) return;

			setNotifications(Array.isArray(sent) ? sent.slice(0, RECENT_LIMIT) : []);
			setOrders(sales.data ?? []);
			setLoading(false);
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [customer.id]);

	const levelSection = (
		<CustomerLevelSection
			customer={customer}
			onCustomerChange={onCustomerChange}
			hasUnsavedChanges={hasUnsavedChanges}
		/>
	);

	if (loading) {
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				{levelSection}
				<InlineLoading message="Loading customer overview…" minHeight={200} />
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{levelSection}

			<SectionCard
				title={
					<Space>
						<BellOutlined style={{ fontSize: 12 }} />
						<Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Notifications</Text>
					</Space>
				}
			>
				{notifications.length === 0 ? (
					<EmptyState title="No notifications yet" />
				) : (
					<List
						size="small"
						dataSource={notifications}
						rowKey={(item) => String(item.id)}
						renderItem={(item) => (
							<List.Item>
								<List.Item.Meta
									title={<Text strong style={{ fontSize: 12 }}>{item.template_name || "Notification"}</Text>}
									description={
										<Text type="secondary" style={{ fontSize: 11 }}>
											{item.subject || "No Subject"}
											{item.created_at
												? ` — ${formatInZone(item.created_at, "DD MMM YYYY") || ""}`
												: ""}
										</Text>
									}
								/>
							</List.Item>
						)}
					/>
				)}
			</SectionCard>

			<SectionCard
				title={
					<Space>
						<ShoppingOutlined style={{ fontSize: 12 }} />
						<Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Orders</Text>
					</Space>
				}
			>
				{orders.length === 0 ? (
					<EmptyState title="No orders yet" />
				) : (
					<List
						size="small"
						dataSource={orders}
						rowKey={(order) => String(order.sales_order_id)}
						renderItem={(order) => (
							<List.Item
								actions={[
									<Text key="total" strong style={{ fontSize: 12 }}>
										{customerMoney.format(Number(order.total_amount ?? 0))}
									</Text>,
								]}
							>
								<List.Item.Meta
									title={
										<Text strong style={{ fontSize: 12 }}>
											{order.shopify_order_identifier ?? `#${order.sales_order_id}`}
										</Text>
									}
									description={
										<Text type="secondary" style={{ fontSize: 11 }}>
											{order.order_date
												? formatInZone(order.order_date, "DD MMM YYYY") || "—"
												: "—"}
										</Text>
									}
								/>
							</List.Item>
						)}
					/>
				)}
			</SectionCard>
		</div>
	);
};

export default CustomerOverviewTab;
